import React, {useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {AgGridReact} from "ag-grid-react";
import {Modal} from "../../../../components/modal/modal.jsx";
import GarrisonService from "../../../../services/dispatch/garrison-service.js";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../utils/ag-grid-columns-utils.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import Loader from "../../../../components/template/loader.jsx";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;

const hasValue = value => value !== null && value !== undefined && value !== "";

const GarrisonHistoryModal = ({payload, boName, onClose}) => {
    const navigate = navigateHook();
    const requested = Boolean(payload);
    const garrison = payload?.garrison;
    const garrisonId = garrison?.garrisonId;
    const garrisonName = garrison?.garrisonName ?? garrison?.name ?? "";

    const historyQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.garrisonHistory(garrisonId),
        queryFn: () => GarrisonService.getDispatchHistoryByGarrison(garrisonId),
        enabled: requested && hasValue(garrisonId),
    });
    const isReady = !historyQuery.isFetching && (historyQuery.isSuccess || historyQuery.isError);
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    const rows = Array.isArray(historyQuery.data) ? historyQuery.data : [];

    const openDispatch = (id) => {
        const url = `${DESPACHO_WEB_URL}/dispatch/form/${id}?redir=despacho/dashboard`
        navigate(url, true);
    }

    const openBo = (occurrenceNumber) => {
        if (!hasValue(occurrenceNumber)) return "";

        const boNumber = String(occurrenceNumber).replace("/", "-")

        navigate("/bo/redirect/" + boNumber, true)
    }

    const columnDefs = useMemo(() => [
        {
            field: "numberCheckbook",
            headerName: "Talão",
            width: 100,
            comparator: aggridUtils.agGridNumberYearComparator,
            cellRenderer: ({data, value}) => {
                return (<p className="cursor-pointer text-[#428bca] hover:underline" onClick={() => openDispatch(data?.id)}>
                    {value ?? ""}
                </p>)
            },
        },
        {
            field: "occurrenceNumber",
            headerName: boName,
            width: 100,
            comparator: aggridUtils.agGridNumberYearComparator,
            cellRenderer: ({value}) => (
                value ? (
                    <p className="text-[#428bca] hover:underline" onClick={() => openBo(value)}>
                        {value}
                    </p>
                ) : ""
            ),
        },
        {field: "start", headerName: "Início", width: 130, comparator: aggridUtils.agGridCompareDateTime},
        {field: "end", headerName: "Fim", width: 130, comparator: aggridUtils.agGridCompareDateTime},
        {field: "nature", headerName: "Natureza", flex: 1, minWidth: 120},
        {field: "place", headerName: "Local pré-cadastrado", flex: 1, minWidth: 140},
        {field: "neighborhood", headerName: "Bairro", flex: 1, minWidth: 120},
        {field: "street", headerName: "Logradouro", flex: 1, minWidth: 120},
        {field: "number", headerName: "Nº", width: 90},
        {
            field: "sourceMessage",
            headerName: "Origem",
            flex: 1,
            minWidth: 180,
            wrapText: true,
            autoHeight: true,
            tooltipField: "sourceMessage",
        },
    ], [boName]);

    const openGarrison = () => {
        const url = `${DESPACHO_WEB_URL}/garrison/form/${garrisonId}?redir=despacho/dashboard`
        navigate(url, true);
    }

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title="Histórico de despachos" className="w-11/12" isCloseClick={!historyQuery.isFetching} animated={false}>
                <Modal.Body className="flex max-h-[calc(100vh-180px)] flex-col gap-4 overflow-auto">
                    <div>
                        <p className="mb-1 text-[16px] font-semibold text-[#676a6c]">Guarnição</p>
                        <p className="cursor-pointer text-[#428bca] hover:underline" onClick={openGarrison}>{garrisonName}</p>
                    </div>

                    {historyQuery.isError && (
                        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            Não foi possível carregar o histórico da guarnição.
                        </div>
                    )}

                    {!historyQuery.isFetching && !historyQuery.isError && rows.length === 0 && (
                        <h4 className="text-center text-[16px] font-semibold text-[#676a6c]">A guarnição ainda não possui despachos</h4>
                    )}

                    {!historyQuery.isFetching && !historyQuery.isError && rows.length > 0 && (
                        <AgGridReact
                            domLayout="autoHeight"
                            rowData={rows}
                            columnDefs={columnDefs}
                            defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme()}
                            overlayNoRowsTemplate="Nenhum registro."
                        />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default GarrisonHistoryModal;
